
import { useAdminDashboard } from "@/hooks/use-admin-queries";
import { IstClock } from "@/components/attendance/ist-clock";
import { ActionInboxCard } from "@/components/admin/action-inbox-card";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { formatTimeIST } from "@/lib/timezone";
import { CATEGORY_LABELS, type DashboardKpis, type DevoteeAttendance } from "@/lib/types";
import { AttendanceCellBadge } from "@/components/attendance/attendance-cell";
import { Badge } from "@/components/ui/badge";
import { MessageSquareWarning } from "lucide-react";

function KpiCard({
  label,
  value,
  className,
}: {
  label: string;
  value: number;
  className?: string;
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">
          {label}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className={`text-3xl font-bold tabular-nums ${className ?? ""}`}>
          {value}
        </p>
      </CardContent>
    </Card>
  );
}

export default function AdminDashboardPage() {
  const { data, isLoading, error } = useAdminDashboard();

  if (isLoading) {
    return <p className="text-muted-foreground">Loading dashboard…</p>;
  }

  if (error || !data) {
    return (
      <p className="text-red-600">
        Could not load dashboard. Check your API key and connection.
      </p>
    );
  }

  const groupBlocks =
    data.group_attendance && data.group_attendance.length > 0
      ? data.group_attendance
      : data.devotee_attendance
        ? [data.devotee_attendance]
        : [];

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-2xl font-semibold">Dashboard</h2>
          <p className="text-sm text-muted-foreground">Today&apos;s overview</p>
        </div>
        <div className="sm:min-w-[200px] sm:text-right">
          <IstClock />
        </div>
      </div>

      {data.action_inbox && <ActionInboxCard inbox={data.action_inbox} />}

      {groupBlocks.map((block) => (
        <GroupAttendanceCard
          key={block.category ?? block.label}
          block={block}
        />
      ))}

      <div>
        <h3 className="mb-3 text-lg font-semibold">Employees</h3>
        <KpiGrid kpis={data.kpis} />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Live punch feed</CardTitle>
        </CardHeader>
        <CardContent>
          {data.recent_punches.length === 0 ? (
            <p className="text-sm text-muted-foreground">No punches yet today.</p>
          ) : (
            <ul className="divide-y">
              {data.recent_punches.map((p) => (
                <li
                  key={p.id}
                  className="flex items-center justify-between gap-3 py-3 text-sm"
                >
                  <div>
                    <p className="font-medium">{p.person_name}</p>
                    <p className="text-muted-foreground">
                      {CATEGORY_LABELS[p.category]} · {formatTimeIST(p.punched_at)}
                    </p>
                  </div>
                  <AttendanceCellBadge
                    showWord
                    cell={{
                      status: p.status,
                      late_minutes: p.late_minutes,
                      verify_mode: p.verify_mode,
                    }}
                  />
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function KpiGrid({ kpis }: { kpis: DashboardKpis }) {
  return (
    <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7">
      <KpiCard label="Present" value={kpis.present} className="text-emerald-700" />
      <KpiCard label="Late" value={kpis.late} className="text-amber-700" />
      <KpiCard label="Absent" value={kpis.absent} className="text-red-700" />
      <KpiCard
        label="Official leave (O)"
        value={kpis.on_leave}
        className="text-sky-700"
      />
      <KpiCard
        label="Sick (S)"
        value={kpis.sick ?? 0}
        className="text-rose-700"
      />
      <KpiCard
        label="Permission (TP)"
        value={kpis.taken_permission ?? 0}
        className="text-teal-700"
      />
      <KpiCard label="Pending" value={kpis.pending} className="text-slate-600" />
    </div>
  );
}

function GroupAttendanceCard({ block }: { block: DevoteeAttendance }) {
  const subtitle =
    block.category === "PDC" || block.category === "BCARIS"
      ? "Same morning schedule as Yoga"
      : block.category === "YOGA"
        ? "Morning program"
        : "Morning attendance";

  return (
    <Card className="border-saffron-200">
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <CardTitle>{block.label}</CardTitle>
            <p className="text-sm text-muted-foreground">{subtitle}</p>
          </div>
          <Badge variant="secondary">{block.attendees.length} enrolled</Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <KpiGrid kpis={block.kpis} />
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full min-w-[360px] text-sm">
            <thead>
              <tr className="border-b bg-muted/40">
                <th className="px-3 py-2 text-left font-medium">Name</th>
                <th className="px-3 py-2 text-center font-medium">Morning</th>
              </tr>
            </thead>
            <tbody>
              {block.attendees.map((a) => (
                <tr
                  key={`${a.person_id}-${a.category}`}
                  className="border-b last:border-0"
                >
                  <td className="px-3 py-2 font-medium">{a.person_name}</td>
                  <td className="px-3 py-2 text-center">
                    <span className="inline-flex items-center justify-center gap-1">
                      <AttendanceCellBadge
                        showWord
                        cell={{
                          status: a.status,
                          late_minutes: a.late_minutes,
                          first_punch_at: a.first_punch_at,
                        }}
                      />
                      {a.has_notice && (
                        <MessageSquareWarning
                          className="h-3.5 w-3.5 text-saffron-600"
                          aria-label="Notice submitted"
                        />
                      )}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
